import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { portalPath } from '../../domain/identity';
import {
  familyPortalBookPath, familyPortalDeletedPath, familyPortalJoinPath, familyPortalOpen, familyPortalSchedulePath,
  familyPortalSignInPath,
  familyPortalWord as w,
} from '../../domain/familyPortal';
import PortalPage from '../../pages/PortalPage';
import PortalJoinPage from '../../pages/PortalJoinPage';
import PortalSignInPage from '../../pages/PortalSignInPage';
import PortalCard from './PortalCard';
import s from '../../pages/PortalPage.module.css';

// Everything under /portal (#772): the Family Portal, as its own small app.
//
// Mounted by src/App.jsx **outside** the teacher's app — above SignInGate,
// StudioGate and every studio provider — so none of the teacher's frame
// appears here: no sidebar, no Paywall, no trial wording, and no studio
// listener opened for a family (#543 §7: a `PortalApp` outside every studio
// provider). StudioGate mounts it too, for a portal login that asked for any
// other path, which this then sends to /portal.
//
// The pages are chosen by path rather than by a nested <Routes>, so the same
// component answers wherever it is mounted (under "/portal/*" in App, under
// "*" in StudioGate):
//
//   /portal/join      the invitation's link (#770), which handles its own
//                     closed, signed-out and signed-in cases
//   /portal/signin    a family signing in again: email and password and
//                     "Forgot your password?", no trial and no sign-up (#771)
//   /portal           Home, /portal/schedule the Schedule and /portal/book
//                     Find & Book (#773) — signed in
//   /portal/deleted   after a family deletes its own login (#774)
//   anything else     /portal
//
// **While the Family Portal is closed** (#769, core/src/family_portal.rs)
// every page but the join page (which says so itself) is the heading, one
// sentence and, signed in, Sign out: a portal login still lands here
// (#764's gates do not follow the switch), and reads nothing.
export default function PortalApp() {
  const { pathname } = useLocation();
  const { user, loading, logout } = useAuth();
  const path = pathname.replace(/\/+$/, '') || '/';

  // Mounted by StudioGate on a teacher's path: a portal login has none.
  if (path !== portalPath() && !path.startsWith(`${portalPath()}/`)) return <Navigate to={portalPath()} replace />;
  if (path === familyPortalJoinPath()) return <PortalJoinPage />;

  if (!familyPortalOpen()) {
    return (
      <PortalCard>
        <p className={s.body}>{w('closedBody')}</p>
        {user ? <SignOut logout={logout} /> : null}
      </PortalCard>
    );
  }

  // After "Delete my login" (#774, PortalDeleteLogin.jsx): signed out, so
  // before the sign-in redirect.
  if (path === familyPortalDeletedPath()) {
    return (
      <PortalCard title={w('deleteLoginDoneTitle')}>
        <p className={s.body}>{w('deleteLoginDoneBody')}</p>
      </PortalCard>
    );
  }

  if (loading) return <PortalCard><p className={s.body}>{w('loading')}</p></PortalCard>;

  if (path === familyPortalSignInPath()) {
    return user ? <Navigate to={portalPath()} replace /> : <PortalSignInPage />;
  }
  if (!user) return <Navigate to={familyPortalSignInPath()} replace />;

  if (path === portalPath()) return <PortalPage tab="home" />;
  if (path === familyPortalSchedulePath()) return <PortalPage tab="schedule" />;
  if (path === familyPortalBookPath()) return <PortalPage tab="book" />;
  return <Navigate to={portalPath()} replace />;
}

function SignOut({ logout }) {
  const signOut = () => logout().then(() => {}, (err) => {
    console.error('[portal] sign-out failed:', err);
  });
  return <button type="button" className={s.linkButton} onClick={signOut}>{w('signOut')}</button>;
}
